import { useEffect, useMemo, useState } from "react";
import { ApiError, api } from "@shared/api";
import type { Uuid } from "@shared/api";
import { Dialog } from "@shared/ui/bits";
import { Button } from "@shared/ui/button";
import { Input } from "@shared/ui/input";
import { Permission, has } from "@entities/role";
import { isFeed } from "@entities/channel";
import { useStore } from "@features/chat";

/**
 * «Подписаться» на канал объявлений — как «Follow» в Discord: выбрать свой
 * канал, куда новости будут приходить от вебхука. Серверы — только те, где
 * можно заводить вебхуки; запрет на отдельный канал скажет сам сервер.
 */
export function FollowDialog({
  channelId,
  channelName,
  onClose,
}: {
  channelId: Uuid;
  channelName: string;
  onClose: () => void;
}) {
  const store = useStore();
  const [allowed, setAllowed] = useState<Set<Uuid> | null>(null);
  const [query, setQuery] = useState("");
  const [target, setTarget] = useState<Uuid | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [done, setDone] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    for (const server of store.servers) {
      if (!store.states[server.id]) void store.loadServer(server.id);
    }
    void Promise.all(
      store.servers.map((server) =>
        api.permissions
          .onServer(server.id)
          .then((rights) => (rights.is_owner || has(rights.permissions, Permission.ManageWebhooks) ? server.id : null))
          .catch(() => null),
      ),
    ).then((ids) => alive && setAllowed(new Set(ids.filter((id): id is Uuid => !!id))));
    return () => {
      alive = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const targets = useMemo(
    () =>
      store.servers
        .filter((server) => allowed?.has(server.id))
        .flatMap((server) =>
          (store.states[server.id]?.channels ?? [])
            .filter((channel) => isFeed(channel.channel_type) && channel.id !== channelId)
            .map((channel) => ({ id: channel.id, name: `#${channel.name}`, where: server.name })),
        ),
    [store.servers, store.states, allowed, channelId],
  );
  const needle = query.trim().toLowerCase();
  const shown = targets.filter((item) => !needle || `${item.name} ${item.where}`.toLowerCase().includes(needle)).slice(0, 40);

  async function follow() {
    if (!target) return;
    setBusy(true);
    setProblem(null);
    try {
      await api.webhooks.follow(channelId, target);
      const picked = targets.find((item) => item.id === target);
      setDone(picked ? `${picked.name} · ${picked.where}` : "выбранный канал");
    } catch (error) {
      setProblem(
        error instanceof ApiError && error.code === "already_following"
          ? "Этот канал уже получает отсюда объявления"
          : error instanceof ApiError && error.code === "webhook_limit"
          ? "В этом канале уже десять вебхуков — уберите лишний в его настройках"
          : error instanceof ApiError && error.status === 403
            ? "В этом канале нельзя заводить вебхуки"
            : error instanceof ApiError
              ? error.message
              : "Не вышло подписаться",
      );
    } finally {
      setBusy(false);
    }
  }

  if (done) {
    return (
      <Dialog
        title="Подписка оформлена"
        onClose={onClose}
        actions={
          <Button type="button" onClick={onClose}>
            Готово
          </Button>
        }
      >
        <p className="small" style={{ margin: 0 }}>
          Опубликованное в #{channelName} будет приходить в {done}. Отписаться — удалить вебхук в настройках того
          канала.
        </p>
      </Dialog>
    );
  }

  return (
    <Dialog
      title={`Подписаться на #${channelName}`}
      onClose={onClose}
      actions={
        <>
          <Button variant="secondary" type="button" onClick={onClose}>
            Отмена
          </Button>
          <Button type="button" disabled={!target || busy} onClick={() => void follow()}>
            Подписаться
          </Button>
        </>
      }
    >
      <p className="small muted" style={{ margin: 0 }}>
        Объявления, которые здесь опубликуют, придут в выбранный канал вашего сервера.
      </p>
      <Input
        value={query}
        placeholder="Куда: канал вашего сервера"
        aria-label="Куда присылать объявления"
        onChange={(event) => setQuery(event.target.value)}
      />
      <div className="forward-list" role="radiogroup" aria-label="Куда присылать объявления">
        {shown.map((item) => (
          <button
            key={item.id}
            type="button"
            role="radio"
            aria-checked={target === item.id}
            className={`switcher-item ${target === item.id ? "on" : ""}`}
            onClick={() => setTarget(item.id)}
          >
            <span className="switcher-name ellipsis">{item.name}</span>
            <span className="switcher-where ellipsis">{item.where}</span>
          </button>
        ))}
        {allowed && shown.length === 0 && (
          <div className="switcher-none">
            {targets.length === 0 ? "Нет серверов, где вы можете заводить вебхуки" : "Ничего не нашлось"}
          </div>
        )}
      </div>
      {problem && <p className="small danger-text" style={{ margin: 0 }}>{problem}</p>}
    </Dialog>
  );
}
